import React, { useEffect } from 'react';
import StoreIcon from '@mui/icons-material/Store';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import EditIcon from '@mui/icons-material/Edit';
import { useSession } from '../contexts/SessionContext';
import { formatAddressLines } from '../utils/address';
import { formatServicePrice, STARTING_AT_LABEL } from '../utils/serviceDisplay';
import {
  VISIT_TYPES,
  VISIT_TYPE_OPTION_DESCRIPTIONS,
  VISIT_TYPE_OPTION_LABELS,
  VISIT_TYPE_STEP_TITLE,
  VISIT_TYPE_TEST_IDS,
} from '../utils/visitType';

// CIN-7181: the Wait / Drop-off choice step, between Services and Date & Time.
// The chosen location and services sit ABOVE the question (same section markup
// as Review & Confirm) and take the visitor back to change them; the two tap
// targets only select - the footer Next advances. Each tap target is a card
// labelling a VISIBLE native radio (one tab stop, arrow keys move the choice);
// CIN-7180: the radio's name is the card title and its description the card
// copy, so screen readers announce the same name as before. Focus lands on the
// modal title, which carries this step's question (Scheduler).
const VisitTypeSelection = ({ onEditLocation, onEditServices }) => {
  const {
    selectedLocation,
    selectedServices,
    visitType,
    setVisitType,
    setSelectedTimeSlot,
    queueAnalyticsEvent,
  } = useSession();

  // Arrival event. queueAnalyticsEvent is stable and de-duplicates by name, so
  // StrictMode's double-mounted effect still reports one arrival.
  useEffect(() => {
    queueAnalyticsEvent('visit_type_step_viewed');
  }, [queueAnalyticsEvent]);

  const choose = (value) => {
    if (value !== visitType) {
      // Waiter and drop-off grids differ: a time picked for the other type
      // would otherwise be saved (and booked) under this one.
      setSelectedTimeSlot(null);
      setVisitType(value);
    }
    queueAnalyticsEvent('visit_type_selected');
  };

  // A context section is a button when it can navigate back, plain otherwise
  // (e.g. the location on a single-location calendar). The button's
  // accessible name is its visible content (title, name, address / services)
  // led by a visually hidden "Change".
  const contextSection = ({ testId, onEdit, icon, title, body }) => {
    const content = (
      <>
        <span className="recap-header">
          {icon}
          {onEdit && <span className="visit-type-visually-hidden">Change </span>}
          <span className="visit-type-context-title">{title}</span>
          {onEdit && <EditIcon className="visit-type-context-edit" aria-hidden="true" />}
        </span>
        <span className="visit-type-context-body">{body}</span>
      </>
    );
    if (onEdit) {
      return (
        <button
          type="button"
          className="recap-section visit-type-context visit-type-context-button"
          data-testid={testId}
          onClick={onEdit}
        >
          {content}
        </button>
      );
    }
    return (
      <div className="recap-section visit-type-context" data-testid={testId}>
        {content}
      </div>
    );
  };

  return (
    <div className="step-content visit-type-selection">
      {selectedLocation && contextSection({
        testId: 'visit-type-context-location',
        onEdit: onEditLocation,
        icon: <StoreIcon className="recap-header-icon" />,
        title: 'Location',
        body: (
          <>
            <strong className="visit-type-context-name">{selectedLocation.name}</strong>
            {formatAddressLines(selectedLocation).map((line, index) => (
              <span key={index} className="visit-type-context-line">{line}</span>
            ))}
          </>
        ),
      })}

      {selectedServices.length > 0 && contextSection({
        testId: 'visit-type-context-services',
        onEdit: onEditServices,
        icon: <MiscellaneousServicesIcon className="recap-header-icon" />,
        title: 'Services',
        body: selectedServices.map((service) => {
          const priceText = formatServicePrice(service);
          return (
            <span key={service.id} className="visit-type-context-service" data-testid="visit-type-context-service">
              <strong className="visit-type-context-name">{service.name}</strong>
              {priceText && (
                <span className="visit-type-context-line">{STARTING_AT_LABEL} {priceText}</span>
              )}
            </span>
          );
        }),
      })}

      <div
        className="visit-type-options"
        role="radiogroup"
        aria-label={VISIT_TYPE_STEP_TITLE}
        data-testid="visit-type-options"
      >
        {VISIT_TYPES.map((type) => {
          const selected = visitType === type;
          const testId = VISIT_TYPE_TEST_IDS[type];
          const labelId = `visit-type-option-label-${testId}`;
          const descriptionId = `visit-type-option-desc-${testId}`;
          return (
            <label
              key={type}
              className={`visit-type-option${selected ? ' selected' : ''}`}
              data-testid={`visit-type-option-${testId}`}
            >
              <input
                type="radio"
                className="visit-type-option-input"
                name="visit-type"
                value={type}
                checked={selected}
                aria-labelledby={labelId}
                aria-describedby={descriptionId}
                data-testid={`visit-type-radio-${testId}`}
                onChange={() => choose(type)}
                // A click on the already-checked option fires no change but
                // still reports the selection, as the tap target did.
                onClick={() => { if (type === visitType) choose(type); }}
              />
              <span id={labelId} className="visit-type-option-label">{VISIT_TYPE_OPTION_LABELS[type]}</span>
              <span
                id={descriptionId}
                className="visit-type-option-description"
                data-testid={`visit-type-option-description-${testId}`}
              >
                {VISIT_TYPE_OPTION_DESCRIPTIONS[type]}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
};

export default VisitTypeSelection;
